<template>
	<div
		ref="searchRootRef"
		class="search"
		:class="{ 'is-open': isSearchOpen }"
		:style="{ '--horizontal-offset': `${horizontalOffset}px` }"
	>
		<div class="search-bar">
			<div class="box">
				<span
					class="icon"
					:aria-label="isSearchOpen ? t('search.close') : t('search.open')"
					@click="isSearchOpen ? closeSearch() : openSearch()"
				>
					<AppIcon :name="IconName.Search" :size="18" :stroke-width="2" />
				</span>
				<div class="search-fields">
					<ArkListboxDropdown
						v-model="selectedSearchTypeId"
						:options="searchTypes"
						:multiple="false"
						:clearable="false"
						:placeholder="t('search.searchBy')"
						class="search-type-dropdown"
						@click="userHasManuallySelectedType = true"
					/>
					<div class="divider"></div>
					<div class="input-wrapper">
						<input
							v-model="searchInput"
							ref="searchInputRef"
							type="search"
							class="input"
							:placeholder="currentSearchType.placeholder"
							@keydown.enter="handleEnterKey()"
							@click="handleInputClick"
							@blur="focusOutHandler"
						/>
						<button
							v-if="searchInput"
							type="button"
							class="clear-button"
							:aria-label="t('search.clear')"
							@mousedown.stop.prevent="clearSearchInput"
						>
							×
						</button>
					</div>
				</div>
			</div>

			<div v-show="isSearchOpen && showSuggestions && suggestions.length > 0" class="suggestions">
				<div
					v-for="suggestion in suggestions"
					:key="getSuggestionKey(suggestion)"
					class="suggestion"
					@click="handleSuggestionClick(suggestion)"
				>
					<slot name="suggestionIcon"></slot>
					{{ getSuggestionDisplay(suggestion) }}
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { LngLatBounds } from "mapbox-gl"
import debounce from "lodash.debounce"
import { ref, watch, PropType, computed, nextTick, onMounted, onUnmounted } from "vue"
import { useI18n } from "vue-i18n"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import * as addressClient from "@/components/map/addressClient"
import { getParcel, getTransactionsByInscriptionNumber } from "@/utils/ApiClient"
import { UseRootStore, Permission } from "@/model/RootStore"
import { config, Feature } from "@/AppConfig.ts"
import { storeToRefs } from "pinia"
import type { Transaction } from "@/model/DataModel.ts"
import { posthogClient } from "@/utils/posthogClient"
import { detectSearchType, normalizeTransactionInscriptionNumber, SearchType } from "@/types/searchType"

import ArkListboxDropdown from "../ui/ArkListboxDropdown.vue"

export type AddressPosition = {
	lng: number
	lat: number
	bounds?: LngLatBounds
	isRegion: boolean
}

export type ParcelPosition = {
	type: string
	lng: number
	lat: number
}

const props = defineProps({
	mapCenter: {
		type: Object as PropType<{ lat: number; lng: number }>,
		required: true,
	},
	horizontalOffset: {
		type: Number,
		default: 0,
	},
})

const emit = defineEmits(["search-by-address", "search-by-parcel", "search-by-transaction", "search-by-opportunity"])

const store = UseRootStore()
const { userApiToken, currentUser } = storeToRefs(store)

const { t } = useI18n()

// unified vars
const searchInput = ref("")
const searchInputRef = ref<HTMLInputElement | null>(null)
const addressSuggestions = ref<addressClient.DiscoverResultItem[]>([])
const showSuggestions = ref(false)
const userHasManuallySelectedType = ref<boolean>(false)
const isSearchOpen = ref(false)
const searchRootRef = ref<HTMLElement | null>(null)

// keep in sync with the @media (max-width: 600px) breakpoint below
const compactLabelQuery = window.matchMedia("(max-width: 600px)")
const isCompactLabel = ref(compactLabelQuery.matches)
const handleCompactLabelChange = (event: MediaQueryListEvent) => {
	isCompactLabel.value = event.matches
}

onMounted(() => compactLabelQuery.addEventListener("change", handleCompactLabelChange))
onUnmounted(() => compactLabelQuery.removeEventListener("change", handleCompactLabelChange))

function openSearch() {
	isSearchOpen.value = true
	nextTick(() => searchInputRef.value?.focus())
}

function closeSearch() {
	isSearchOpen.value = false
	showSuggestions.value = false
}

function clearSearchInput() {
	searchInput.value = ""
	addressSuggestions.value = []
	showSuggestions.value = false
	searchInputRef.value?.focus()
}

const handleClickOutside = (event: MouseEvent) => {
	if (!isSearchOpen.value || searchInput.value.trim()) return
	if (searchRootRef.value && !searchRootRef.value.contains(event.target as Node)) {
		closeSearch()
	}
}

onMounted(() => document.addEventListener("mousedown", handleClickOutside))
onUnmounted(() => document.removeEventListener("mousedown", handleClickOutside))
const selectedSearchTypeId = ref<SearchType>(SearchType.ADDRESS)

// search types
const searchTypes = computed(() => {
	const types = [
		{
			value: SearchType.ADDRESS,
			label: t("search.byAddress"),
			placeholder: t("search.byAddressPlaceholder"),
			action: "searchByAddress",
		},
		{
			value: SearchType.PARCEL,
			label: isCompactLabel.value ? t("search.byParcelShort") : t("search.byParcel"),
			placeholder: isCompactLabel.value ? t("search.byParcelPlaceholderShort") : t("search.byParcelPlaceholder"),
			action: "searchByParcel",
		},
	]

	if (currentUser.value.has(Permission.OpportunityRead)) {
		types.push({
			value: SearchType.OPPORTUNITY,
			label: t("search.byOpportunity"),
			placeholder: isCompactLabel.value
				? t("search.byOpportunityPlaceholderShort")
				: t("search.byOpportunityPlaceholder"),
			action: "searchByOpportunity",
		})
	}

	if (currentUser.value.has(Permission.TransactionRead) && config.ui.has(Feature.Transactions)) {
		types.push({
			value: SearchType.TRANSACTION,
			label: isCompactLabel.value ? t("search.byTransactionShort") : t("search.byTransaction"),
			placeholder: isCompactLabel.value
				? t("search.byTransactionPlaceholderShort")
				: t("search.byTransactionPlaceholder"),
			action: "searchByTransaction",
		})
	}

	return types
})

const currentSearchType = computed(() => {
	return searchTypes.value.find(type => type.value === selectedSearchTypeId.value) || searchTypes.value[0]
})

const suggestions = computed(() => {
	switch (currentSearchType.value.value) {
		case "address":
			return addressSuggestions.value || []
		case "parcel":
		case "transaction":
		default:
			return []
	}
})

const handleSearchByOpportunity = debounce(async (opportunityId: string) => {
	searchInputRef.value?.blur()

	const trimmedId = opportunityId.trim()
	if (!trimmedId) return

	emit("search-by-opportunity", trimmedId)
}, 200)

const handleSearchByAddress = (_event?: unknown, suggestion = addressSuggestions.value[0]) => {
	if (!suggestion) return

	showSuggestions.value = false
	searchInput.value = suggestion.title
	searchInputRef.value?.blur()

	const { resultType, position, mapView } = suggestion
	emit("search-by-address", {
		lng: position.lng,
		lat: position.lat,
		bounds: mapView && new LngLatBounds([mapView.west, mapView.south], [mapView.east, mapView.north]),
		isRegion: addressClient.isRegion(resultType),
	} as AddressPosition)
}

const handleSearchByParcel = debounce(async (searchValue: string) => {
	searchInputRef.value?.blur()

	const parcelId = searchValue.replace(/\s+/g, "") // remove all whitespace

	if (!parcelId) return

	const parcel = await getParcel(parcelId as unknown as number)
	if (!parcel) {
		alert(t("search.invalidParcelNumber"))
		return
	}

	const { longitude: lng, latitude: lat } = parcel.location.coordinates
	emit(
		"search-by-parcel",
		{
			type: "parcelNumber",
			lng: Number(lng),
			lat: Number(lat),
		} as ParcelPosition,
		parcel.parcelID
	)
}, 200)

const handleSearchByTransaction = debounce(async (inscriptionNumber: string, transaction?: Transaction) => {
	searchInputRef.value?.blur()
	const normalizedInscriptionNumber = normalizeTransactionInscriptionNumber(inscriptionNumber)

	if (!transaction) {
		const transactions = await getTransactionsByInscriptionNumber(userApiToken.value, normalizedInscriptionNumber)
		transaction = transactions[0]

		if (!transaction) {
			alert(t("search.invalidTransactionInscriptionNumber"))
			return
		}
	}

	emit("search-by-transaction", transaction)
}, 200)

/**
 * Normalize inscription number into "XX XXX XXX".
 * Examples:
 *
 *		"28 767 334"		#=> "28 767 334"
 * 		"28767334"   		#=> "28 767 334"
 * 		"  28  767   334 "	#=> "28 767 334"
 */
function normalizeInscriptionNumber(num: string): string {
	const digits = (num ?? "").replace(/\D+/g, "")
	if (digits.length !== 8) return num

	return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)}`
}

watch(
	() => store.searches.transaction,
	num => {
		if (!num) return
		currentSearchType.value.action = "searchByTransaction"
		searchInput.value = normalizeInscriptionNumber(num)
		handleSearch()
	},
	{ immediate: true }
)

watch(
	() => store.searches.parcel,
	parcelId => {
		if (!parcelId) return
		currentSearchType.value.action = "searchByParcel"
		searchInput.value = parcelId
		handleSearch()
	},
	{ immediate: true }
)

watch(
	() => store.searches.opportunity,
	id => {
		if (!id) return
		currentSearchType.value.action = "searchByOpportunity"
		searchInput.value = id
	},
	{ immediate: true }
)

function handleSearch(suggestionData?: any) {
	const action = currentSearchType.value.action
	const input = suggestionData || searchInput.value

	switch (action) {
		case "searchByAddress":
			handleSearchByAddress(undefined, input)
			break
		case "searchByParcel":
			handleSearchByParcel(input)
			break
		case "searchByTransaction":
			handleSearchByTransaction(input, suggestionData)
			break
		case "searchByOpportunity":
			handleSearchByOpportunity(input)
			break
	}

	showSuggestions.value = false
}

function handleUserSearch(suggestionData?: any): void {
	const searchType = selectedSearchTypeId.value
	const value = searchInput.value

	handleSearch(suggestionData)
	posthogClient.searchInteracted(searchType, value)
}

const handleEnterKey = () => {
	if (suggestions.value.length > 0 && currentSearchType.value.value === "address") {
		handleUserSearch(suggestions.value[0])
	} else {
		handleUserSearch()
	}
}

const handleInputClick = () => {
	if (currentSearchType.value.value === "address") {
		showSuggestions.value = true
	}
}

const focusOutHandler = () => {
	setTimeout(() => {
		showSuggestions.value = false
	}, 200)
}

const handleSuggestionClick = (suggestion: any) => {
	handleUserSearch(suggestion)
}

const getSuggestionKey = (suggestion: any) => {
	switch (currentSearchType.value.value) {
		case "address":
			return suggestion.id
		default:
			return suggestion
	}
}

const getSuggestionDisplay = (suggestion: any) => {
	switch (currentSearchType.value.value) {
		case "address":
			return suggestion.address?.label
		default:
			return suggestion
	}
}

const suggestionsHandler = debounce(async () => {
	addressSuggestions.value = []

	const inputAddress = searchInput.value.trim()
	if (!inputAddress) return

	addressSuggestions.value = await addressClient.getSuggestions(inputAddress, props.mapCenter, 10)
}, 300)

watch(searchInput, async () => {
	if (!userHasManuallySelectedType.value && searchInput.value.trim()) {
		const detectedType = detectSearchType(searchInput.value)
		if (detectedType && searchTypes.value.find(type => type.value === detectedType)) {
			selectedSearchTypeId.value = detectedType
		}
	}
	if (currentSearchType.value.value === "address") {
		await suggestionsHandler()
	}
})

watch(selectedSearchTypeId, async newType => {
	showSuggestions.value = false
	addressSuggestions.value = []

	// if the new type is address, it will show again the suggestions
	if (newType === "address" && searchInput.value.trim()) {
		await suggestionsHandler()
		showSuggestions.value = true
	}
})

watch(
	searchTypes,
	newTypes => {
		if (!newTypes.find((type: any) => type.value === selectedSearchTypeId.value)) {
			selectedSearchTypeId.value = newTypes[0]?.value || "parcel"
		}
	},
	{ immediate: true }
)
</script>

<style scoped>
.search {
	position: fixed;
	top: var(--spacing-md);
	left: calc(
		var(--current-nav-width, 240px) + var(--left-panel-open, 0) * var(--width-left-panel, 380px) +
			var(--horizontal-offset, 0px) + var(--spacing-md)
	);
	z-index: 10;
	width: var(--space-40);
	font-size: var(--font-size-small);

	transition:
		width 0.3s cubic-bezier(0.4, 0, 0.2, 1),
		left 0.3s ease;

	&.is-open {
		width: var(--width-search-input);
	}

	.search-bar {
		position: relative;
	}

	.box {
		display: flex;
		background-color: var(--neutral-0);
		/* clamps to a circle when closed (40px) and a full pill once expanded */
		border-radius: var(--border-radius-full);
		margin-bottom: var(--spacing-xs);
		box-shadow: var(--shadow-lg);
		align-items: center;

		/* visually matches HomePage.vue's .filter-reopen-btn */
		.icon {
			display: flex;
			justify-content: center;
			align-items: center;
			flex-shrink: 0;
			width: var(--space-40);
			height: var(--space-40);
			color: var(--neutral-600);
			opacity: 0.8;
			cursor: pointer;

			&:hover {
				opacity: 1;
			}
		}
	}

	.search-fields {
		display: flex;
		align-items: center;
		flex: 1 1 0%;
		min-width: 0;
		margin-left: calc(-1 * var(--spacing-sm));
		overflow: hidden;
	}

	.divider {
		flex-shrink: 0;
		width: 0.5px;
		height: var(--space-16);
		margin-left: var(--spacing-xs);
		background-color: var(--neutral-200);
	}

	&.is-open .search-fields {
		overflow: visible;
	}

	&:not(.is-open) .search-fields {
		display: none;
	}

	.input-wrapper {
		position: relative;
		display: flex;
		align-items: center;
		flex: 1;
		min-width: 0;
	}

	.clear-button {
		position: absolute;
		right: var(--spacing-md);
		display: flex;
		align-items: center;
		justify-content: center;
		width: var(--spacing-md);
		height: var(--spacing-md);
		border: none;
		background: var(--neutral-200);
		border-radius: 50%;
		cursor: pointer;
		color: var(--neutral-900);
		font-size: var(--font-size-small);
		line-height: 1;
		transition: background-color 0.2s ease;

		&:hover {
			background: var(--neutral-400);
		}
	}

	.input {
		background: none;
		position: relative;
		width: 100%;
		height: auto;
		min-height: var(--height-search-input);
		font-size: var(--font-size-regular);
		font-family: var(--font-family-primary);
		border: none;
		outline: none;
		flex: 1;
		min-width: 0;
		padding-left: var(--spacing-sm);
		padding-right: var(--spacing-xl);
		white-space: normal;
		word-wrap: break-word;
		overflow-y: auto;
		resize: none;
	}

	.search-type-dropdown {
		flex-shrink: 0;
		width: auto;
		z-index: 1001;
	}

	.suggestions {
		position: absolute;
		top: 110%;
		left: 0;
		width: 100%;
		background-color: var(--neutral-0);
		max-height: var(--height-search-results);
		box-shadow: var(--shadow-lg);
		border-radius: var(--border-radius-xl);
		overflow: auto;
		z-index: 1000;
	}

	.suggestion {
		display: flex;
		align-items: center;
		column-gap: var(--spacing-sm);
		position: relative;
		text-align: left;
		cursor: pointer;
		padding: var(--spacing-sm) var(--spacing-md);
		font-size: var(--font-size-regular);
		color: var(--neutral-600);

		&:not(:last-of-type)::after {
			content: "";
			position: absolute;
			left: 2%;
			right: 2%;
			bottom: 0;
			background-color: var(--neutral-400);
			height: var(--border-width-thin);
		}

		&:hover {
			background-color: var(--neutral-200);
		}

		&:focus {
			background-color: var(--neutral-200);
			outline: none !important;
		}
		&.has-selection {
			border-color: transparent !important;
		}
	}
}

.search-type-dropdown :deep(.trigger) {
	height: auto;
	width: auto;
	padding-top: var(--spacing-xs);
	padding-bottom: var(--spacing-xs);
	gap: var(--spacing-sm);
	border: none;
	border-radius: var(--border-radius-xl);
	transition: background-color 0.2s ease;
}

.search-type-dropdown :deep(.trigger:hover:not(:disabled)) {
	background-color: var(--neutral-100);
}

.search-type-dropdown :deep(.trigger:focus),
.search-type-dropdown :deep(.trigger:focus-visible) {
	box-shadow: none;
}

.search-type-dropdown :deep(.popover-content) {
	width: var(--width-select-field);
	/* aligns the popover's left edge with the search bar's, offsetting the leading icon it's anchored past */
	margin-left: calc(-1 * var(--space-40) + var(--spacing-sm));
}

.search-type-dropdown :deep(.popover-content[data-placement^="bottom"]) {
	margin-top: var(--space-12);
}

.search-type-dropdown :deep(.trigger .display-text) {
	flex: none;
}

.search-type-dropdown :deep(.trigger .chevron) {
	margin-left: auto;
	margin-right: var(--spacing-xs);
}

.search-type-dropdown :deep(.trigger .chevron:not(.open)) {
	margin-top: -4px;
}

.input::-webkit-search-cancel-button {
	-webkit-appearance: none;
	appearance: none;
}

@media (max-width: 600px) {
	.search {
		left: var(--spacing-sm);
		font-size: var(--font-size-regular);
	}

	.search.is-open {
		/* explicit width (rather than right/auto) so it can transition smoothly, same as desktop */
		width: calc(100vw - 2 * var(--spacing-sm));
	}

	.search-type-dropdown :deep(.trigger .display-text) {
		padding-right: var(--spacing-xs);
	}
}
</style>
